import { bindPlayer } from './loadScore';
import { el } from '../dom';

/**
 * Full-screen score player with a close button overlay.
 *
 * The counterpart of Singline/Sources/Player/PlayerView.swift: the player
 * bundle is loaded same-origin in an iframe and `loadScoreXML` is called on
 * `contentWindow.__player` directly (ADR 0010). The close button sits in the
 * physical top-right corner, to the right of the player's own "Select" control.
 */
export class PlayerView {
  private readonly root: HTMLElement;
  private popHandler: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = el('div', { class: 'player', hidden: 'hidden' });
    parent.append(this.root);
  }

  async open(xml: string | null, onClose: () => void): Promise<void> {
    this.closeInternal();
    this.root.hidden = false;
    this.root.replaceChildren();

    const close = el('button', { class: 'close', type: 'button', 'aria-label': 'Close' });
    close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    const dismiss = () => {
      this.closeInternal();
      onClose();
    };
    close.addEventListener('click', dismiss);

    if (xml == null) {
      const error = el('div', { class: 'player-error' }, 'Could not open the file');
      this.root.append(error, close);
      this.trapBack(dismiss);
      return;
    }

    const frame = el('iframe', {
      title: 'Score player',
      src: './player/index.html',
      allow: 'autoplay; fullscreen',
    });
    this.root.append(frame, close);
    this.trapBack(dismiss);
    void this.lockLandscape();

    await new Promise<void>((resolve, reject) => {
      frame.addEventListener('load', () => resolve(), { once: true });
      frame.addEventListener('error', () => reject(new Error('Could not load the player.')), { once: true });
    });
    const win = frame.contentWindow;
    if (!win) throw new Error('Could not load the player.');
    try {
      await bindPlayer(win, xml);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not load the score.';
      this.root.replaceChildren(el('div', { class: 'player-error' }, message), close);
    }
  }

  closeInternal(): void {
    this.root.hidden = true;
    this.root.replaceChildren();
    if (this.popHandler) {
      window.removeEventListener('popstate', this.popHandler);
      this.popHandler = null;
    }
    if (document.fullscreenElement === this.root) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }

  private trapBack(dismiss: () => void): void {
    history.pushState({ singlinePlayer: true }, '');
    this.popHandler = () => {
      this.popHandler = null;
      dismiss();
    };
    window.addEventListener('popstate', this.popHandler);
  }

  private async lockLandscape(): Promise<void> {
    try {
      await this.root.requestFullscreen();
    } catch {
      // Fullscreen is a nice-to-have; the overlay still covers the viewport.
    }
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (type: string) => Promise<void>;
    };
    try {
      await orientation.lock?.('landscape');
    } catch {
      // Only works in fullscreen on some browsers; ignore.
    }
  }
}
